/**
 * Message bubble component for the chat interface.
 * Modern gradient bubbles with glass AI messages.
 */

import { Chip } from '@coordio/ui';
import { Bot as SmartToyIcon, User as PersonIcon, Settings as SettingsIcon, Wrench as BuildIcon } from 'lucide-react';
import type { AgentMessageDto } from '../../types/session';
import { MessageRole, parseToolCalls } from '../../types/session';
import { AudioMessage } from './AudioMessage';
import { DocumentMessage } from './DocumentMessage';
import { ImageMessage } from './ImageMessage';
import { VideoMessage } from './VideoMessage';

interface MessageBubbleProps {
  message: AgentMessageDto;
}

/**
 * Modern message bubble with gradient for user and glass for AI.
 */
export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const { role, content, createdAt, tokensUsed, cost, fileId, mediaType, processedContentJson, toolCallsJson } = message;
  const isUser = role === MessageRole.User;
  const isSystem = role === MessageRole.System;
  const isAssistant = role === MessageRole.Assistant;
  const toolCalls = isAssistant ? parseToolCalls(toolCallsJson) : [];

  const formatTimestamp = (ts?: string) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return ts;
    }
  };

  const renderAttachment = () => {
    if (!fileId) return null;
    const mt = (mediaType || '').toLowerCase();
    if (mt === 'image') return <ImageMessage fileId={fileId} processedContentJson={processedContentJson} />;
    if (mt === 'audio') return <AudioMessage fileId={fileId} processedContentJson={processedContentJson} />;
    if (mt === 'video') return <VideoMessage fileId={fileId} processedContentJson={processedContentJson} />;
    return <DocumentMessage fileId={fileId} processedContentJson={processedContentJson} />;
  };

  const getRoleIcon = () => {
    if (isUser) return <PersonIcon size={14} />;
    if (isAssistant) return <SmartToyIcon size={14} />;
    if (isSystem) return <SettingsIcon size={14} />;
    return <BuildIcon size={14} />;
  };

  const getRoleLabel = () => {
    if (isUser) return 'You';
    if (isAssistant) return 'AI Assistant';
    if (isSystem) return 'System';
    return 'Tool';
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}
      style={{ animation: 'fade-in 200ms ease-out' }}
    >
      <div className="max-w-[75%] min-w-[200px]">
        {/* Role Label */}
        <div className={`flex items-center gap-2 mb-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
          <div
            className="w-[22px] h-[22px] rounded flex items-center justify-center"
            style={
              isUser
                ? { background: 'var(--gradient-primary)', color: '#fff' }
                : isAssistant
                  ? { background: 'var(--gradient-primary-subtle)', color: 'var(--mui-palette-primary-main)' }
                  : { background: 'rgba(148, 163, 184, 0.1)', color: 'var(--mui-palette-text-secondary)' }
            }
          >
            {getRoleIcon()}
          </div>
          <span className="text-xs font-semibold" style={{ color: 'var(--mui-palette-text-secondary)' }}>
            {getRoleLabel()}
          </span>
          {createdAt && (
            <span className="text-xs" style={{ color: 'var(--mui-palette-text-disabled)' }}>
              {formatTimestamp(createdAt)}
            </span>
          )}
        </div>

        {/* Message Bubble */}
        <div
          className="p-6"
          style={{
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            ...(isUser
              ? {
                  background: 'var(--gradient-primary)',
                  color: '#fff',
                  borderBottomRightRadius: 4,
                }
              : {
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid var(--glass-border)',
                  borderBottomLeftRadius: 4,
                }),
          }}
        >
          {(content || isUser) && (
            <p
              className="text-sm whitespace-pre-wrap leading-relaxed"
              style={{
                color: isUser ? '#fff' : 'var(--mui-palette-text-primary)',
              }}
            >
              {content?.trim() ? content : isUser ? '(empty message)' : content}
            </p>
          )}

          {renderAttachment()}
        </div>

        {/* Persisted tool calls (harness: name + success; classic: name only) */}
        {toolCalls.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 justify-start">
            {toolCalls.map((call, index) => (
              <Chip
                key={call.id || `${call.name}-${index}`}
                variant="outlined"
                color={call.success === false ? 'red' : call.success === true ? 'green' : 'default'}
                label={
                  call.success === false
                    ? `${call.name} — failed`
                    : call.success === true
                      ? `${call.name} — completed`
                      : call.name
                }
              />
            ))}
          </div>
        )}

        {/* Metadata */}
        {(tokensUsed || cost) && (
          <div className={`mt-2 flex gap-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
            {tokensUsed != null && tokensUsed > 0 && (
              <span className="text-[0.6875rem]" style={{ color: 'var(--mui-palette-text-disabled)' }}>
                {tokensUsed.toLocaleString()} tokens
              </span>
            )}
            {cost != null && cost > 0 && (
              <span className="text-[0.6875rem]" style={{ color: 'var(--mui-palette-text-disabled)' }}>
                ${cost.toFixed(4)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
